import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUB_STATUS } from "@/lib/plans";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Building2, Ban, CheckCircle2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function PlatformCompanies() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/platform/companies").then(({ data }) => setCompanies(data)).catch(() => setCompanies([]));
  useEffect(() => { if (user && user.role === "system_admin") load(); }, [user]);

  if (user && user.role !== "system_admin") return <Navigate to="/dashboard" replace />;

  const toggle = async (c) => {
    setBusy(c.id);
    const action = c.active ? "suspend" : "activate";
    try {
      await api.post(`/platform/companies/${c.id}/${action}`);
      toast.success(c.active ? "停止しました" : "有効化しました");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "操作に失敗しました");
    } finally {
      setBusy(null);
    }
  };

  if (!companies) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  const filtered = companies.filter((c) =>
    !q || (c.name || "").toLowerCase().includes(q.toLowerCase()) || (c.admin_email || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="animate-fade-in" data-testid="platform-companies-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-medium tracking-tight">運営管理 — 契約企業</h1>
        <p className="text-sm text-muted-foreground mt-1">全テナント企業の利用状況・契約状態を管理します。</p>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative max-w-sm flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="会社名・管理者メールで検索" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="platform-search" />
        </div>
        <div className="text-sm text-muted-foreground" data-testid="platform-count">{filtered.length} 社</div>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-muted-foreground uppercase tracking-wide">
                <th className="px-4 py-3 font-medium">会社</th>
                <th className="px-4 py-3 font-medium">プラン</th>
                <th className="px-4 py-3 font-medium">契約状態</th>
                <th className="px-4 py-3 font-medium text-right">物件</th>
                <th className="px-4 py-3 font-medium text-right">ユーザー</th>
                <th className="px-4 py-3 font-medium">状態</th>
                <th className="px-4 py-3 font-medium text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const st = SUB_STATUS[c.subscription_status] || SUB_STATUS.active;
                return (
                  <tr key={c.id} className="border-t border-border" data-testid={`company-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-8 h-8 rounded-sm bg-gray-100 flex items-center justify-center shrink-0"><Building2 className="w-4 h-4 text-gray-500" /></span>
                        <div>
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-muted-foreground">{c.admin_email || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{c.plan_name}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-sm border ${st.tone}`}>{st.label}</span></td>
                    <td className="px-4 py-3 text-right">{c.properties}</td>
                    <td className="px-4 py-3 text-right">{c.users}</td>
                    <td className="px-4 py-3">
                      {c.active
                        ? <span className="text-emerald-600 text-xs">稼働中</span>
                        : <span className="text-red-600 text-xs">停止中</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant={c.active ? "outline" : "default"} disabled={busy === c.id}
                        onClick={() => toggle(c)} data-testid={`company-toggle-${c.id}`}
                        className={c.active ? "" : "bg-brand hover:bg-brand-hover text-white"}>
                        {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : c.active ? <><Ban className="w-3.5 h-3.5 mr-1.5" />停止</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />有効化</>}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
